import { useState, useEffect } from 'react';
import {
  Typography, Box, Grid2 as Grid, Card, CardContent, CircularProgress, Alert,
  Button, IconButton, Tooltip, Divider, Chip
} from '@mui/material';
import {
  Person, Business, CalendarMonth, EventAvailable, Add as AddIcon,
  ContentCopy, Notifications as NotificationsIcon,
  Article, ArrowForward, MedicalServices, People, TrendingUp,
  BeachAccess, HourglassEmpty, DoneAll, FiberManualRecord
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dashboardService from '../services/dashboardService';
import { notificationsService } from '../services/notificationsService';
import { useUpdateOnboardingStats } from '../hooks/useOnboarding';
import { useContextualHint } from '../hooks/useContextualHint';
import { useSnackbar } from '../hooks/useSnackbar';
import { cardSectionHeader, cardSectionHeaderText, cardEmptyState } from '../styles/pageStyles';
import { APPOINTMENT_STATUS } from '../constants/statusConfig';
import PageLayout from '../components/ui/PageLayout';
import AppSnackbar from '../components/ui/AppSnackbar';
import { formatDate, formatTime, stripHtml } from '../utils/formatters';
import ServicesManagerDialog from '../components/services/ServicesManagerDialog';

// Copiar para área de transferência
const copyToClipboard = async (text, showMessage) => {
  try {
    await navigator.clipboard.writeText(text);
    showMessage('Link copiado!', 'success');
  } catch {
    showMessage('Erro ao copiar', 'error');
  }
};

// Componente de item de contador
const StatItem = ({ icon: Icon, count, label, color, onClick }) => (
  <Tooltip title={`Ver ${label}`}>
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
        cursor: 'pointer',
        p: 1,
        borderRadius: 1,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
          transform: 'scale(1.05)'
        }
      }}
    >
      <Icon sx={{ fontSize: 24, color: `${color}.main` }} />
      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1rem' }}>{count}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
        {label}
      </Typography>
    </Box>
  </Tooltip>
);

export default function DashboardPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    professionals: 0,
    services: 0,
    persons: 0,
    places: 0,
    schedules: 0,
    appointments: 0,
    recentAppointments: [],
    upcomingAbsences: [],
    scheduleList: [],
    pageList: []
  });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [servicesManagerOpen, setServicesManagerOpen] = useState(false);
  const { snackbar: copyInfo, showSnackbar, hideSnackbar } = useSnackbar();

  useUpdateOnboardingStats(stats, loading);
  const hint = useContextualHint('dashboard', { stats });

  useEffect(() => {
    const load = async () => {
      try {
        const [data, notifs] = await Promise.all([
          dashboardService.getStats(),
          notificationsService.getNotifications(0, 5)
        ]);
        setStats(data);
        setNotifications(notifs);
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar estatísticas');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageLayout themeKey="dashboard" hint={hint} onHintAction={hint?.action?.link ? () => navigate(hint.action.link) : undefined}>
      {/* Grid de 3 colunas com todos os cards */}
      <Grid container spacing={2}>

        {/* Card: Próximos Agendamentos */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'none' } }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={cardSectionHeader}>
                  <EventAvailable fontSize="small" color="primary" />
                  <Typography {...cardSectionHeaderText}>Próximos Agendamentos</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigate('/appointments')}>
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Box>

              {stats.recentAppointments?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {stats.recentAppointments.slice(0, 7).map((apt, idx) => {
                    const statusConfig = APPOINTMENT_STATUS[apt.status] || APPOINTMENT_STATUS.Pending;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <Box key={apt.id}>
                        {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={statusConfig.label}>
                            <StatusIcon sx={{ fontSize: 16 }} color={statusConfig.chipColor} />
                          </Tooltip>
                          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3, flex: 1 }}>
                            <strong>{apt.customerName}</strong> agendou com <strong>{apt.professionalName}</strong> para {formatTime(apt.startTime)} em {formatDate(apt.startTime)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={cardEmptyState}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Nenhum agendamento próximo
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<ArrowForward />} onClick={() => navigate('/appointments')}>
                    Ver Agendamentos
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Últimas Notificações */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'none' } }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={cardSectionHeader}>
                <NotificationsIcon fontSize="small" color="info" />
                <Typography {...cardSectionHeaderText}>Notificações</Typography>
              </Box>

              {notifications?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {notifications.slice(0, 5).map((notif, idx) => (
                    <Box
                      key={notif.id}
                      onClick={() => navigate({ hash: `#notification=${notif.id}` })}
                      sx={{
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: notif.isRead ? 'transparent' : 'action.hover',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Tooltip title={notif.isRead ? 'Lida' : 'Não lida'}>
                          {notif.isRead ? (
                            <DoneAll sx={{ fontSize: 14, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                          ) : (
                            <FiberManualRecord sx={{ fontSize: 10, color: 'primary.main', mt: 0.5, flexShrink: 0 }} />
                          )}
                        </Tooltip>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            lineHeight: 1.3,
                            fontWeight: notif.isRead ? 'normal' : 'bold',
                            color: notif.isRead ? 'text.secondary' : 'text.primary'
                          }}
                          noWrap
                        >
                          <strong>{notif.title}</strong> - {stripHtml(notif.message)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={cardEmptyState}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma notificação
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Próximas Ausências */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'none' } }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={cardSectionHeader}>
                  <BeachAccess fontSize="small" color="error" />
                  <Typography {...cardSectionHeaderText}>Ausências</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigate('/absences')}>
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Box>

              {stats.upcomingAbsences?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {stats.upcomingAbsences.slice(0, 5).map((absence, idx) => (
                    <Box key={absence.id}>
                      {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: absence.isActive ? 'error.light' : 'transparent',
                          borderRadius: 0.5,
                          px: absence.isActive ? 0.5 : 0
                        }}
                      >
                        {absence.isActive ? (
                          <BeachAccess sx={{ fontSize: 14, color: 'error.dark' }} />
                        ) : (
                          <HourglassEmpty sx={{ fontSize: 14, color: 'text.secondary' }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            lineHeight: 1.3,
                            fontWeight: absence.isActive ? 'bold' : 'normal'
                          }}
                        >
                          {absence.professionalName}{absence.reason && ` - ${absence.reason}`} ({formatDate(absence.startDate)} - {formatDate(absence.endDate)})
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={cardEmptyState}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Nenhuma ausência registrada
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/absences')}>
                    Cadastrar Ausência
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Estatísticas */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'none' } }}>
            <CardContent>
              <Box sx={cardSectionHeader}>
                <TrendingUp fontSize="small" color="primary" />
                <Typography {...cardSectionHeaderText}>Estatísticas</Typography>
              </Box>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1
              }}>
                <StatItem icon={Person} count={stats.professionals} label="Profissionais" color="primary" onClick={() => navigate('/professionals')} />
                <StatItem icon={MedicalServices} count={stats.services} label="Serviços" color="secondary" onClick={() => setServicesManagerOpen(true)} />
                <StatItem icon={People} count={stats.persons || 0} label="Pessoas" color="info" onClick={() => navigate('/persons')} />
                <StatItem icon={Business} count={stats.places} label="Locais" color="success" onClick={() => navigate('/places')} />
                <StatItem icon={CalendarMonth} count={stats.schedules} label="Agendas" color="warning" onClick={() => navigate('/schedules')} />
                <StatItem icon={EventAvailable} count={stats.appointments} label="Agendamentos" color="error" onClick={() => navigate('/appointments')} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Agendas */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'none' } }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={cardSectionHeader}>
                <CalendarMonth fontSize="small" color="success" />
                <Typography {...cardSectionHeaderText}>Agendas</Typography>
              </Box>

              {stats.scheduleList?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {stats.scheduleList.slice(0, 5).map((schedule, idx) => (
                    <Box key={schedule.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.3 }} noWrap>
                        {schedule.name} ({schedule.professionalName} - {schedule.placeName})
                      </Typography>
                      <Tooltip title="Copiar link">
                        <IconButton size="small" onClick={() => copyToClipboard(schedule.shareUrl, showSnackbar)}>
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                  <Box sx={{ mt: 1, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button size="small" variant="text" fullWidth onClick={() => navigate('/schedules')} sx={{ justifyContent: 'flex-start' }}>
                      Gerenciar Agendas →
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={cardEmptyState}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Nenhuma agenda criada
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/schedules')}>
                    Criar Agenda
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card: App Mobile */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{
            height: '100%',
            border: '2px solid',
            borderColor: 'primary.main',
            '&:hover': { transform: 'none' },
            animation: 'heartbeat 2.4s ease-in-out infinite',
            '@keyframes heartbeat': {
              '0%': { transform: 'scale(1)', borderColor: 'primary.main' },
              '5%': { transform: 'scale(1.01)' },
              '10%': { transform: 'scale(1)', borderColor: 'primary.main' },
              '15%': { transform: 'scale(1.01)' },
              '20%': { transform: 'scale(1)', borderColor: 'primary.main' },
              '100%': { transform: 'scale(1)', borderColor: 'rgba(25, 103, 210, 0.3)' }
            }
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📱 Novidade: o app do Agendario chegou!
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Seu Agendario agora cabe no bolso. Consulte seus agendamentos a qualquer hora, em qualquer lugar — pelo celular.
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <img src="/qr-code.svg" alt="QR Code App Agendario" style={{ maxWidth: 120, height: 'auto' }} />
              </Box>
              <Typography variant="body2" color="primary" fontWeight="bold">
                Já instalou?
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Páginas */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', '&:hover': { transform: 'none' } }}>
            <CardContent sx={{ pb: 1 }}>
              <Box sx={cardSectionHeader}>
                <Article fontSize="small" color="secondary" />
                <Typography {...cardSectionHeaderText}>Páginas</Typography>
              </Box>

              {stats.pageList?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {stats.pageList.slice(0, 5).map((page, idx) => (
                    <Box key={page.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, overflow: 'hidden' }}>
                        <Typography variant="caption" noWrap>
                          {page.title}
                        </Typography>
                        {!page.isPublished && (
                          <Chip label="Rascunho" size="small" sx={{ height: 16, fontSize: 10 }} color="warning" />
                        )}
                      </Box>
                      <Tooltip title="Copiar link">
                        <IconButton size="small" onClick={() => copyToClipboard(page.shareUrl, showSnackbar)}>
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                  <Box sx={{ mt: 1, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button size="small" variant="text" fullWidth onClick={() => navigate('/pages')} sx={{ justifyContent: 'flex-start' }}>
                      Gerenciar Páginas →
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={cardEmptyState}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Nenhuma página criada
                  </Typography>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/pages')}>
                    Criar Página
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Card: 🚀 Primeiros Passos - Mostrar apenas se não tem agendas */}
        {stats.scheduleList?.length === 0 && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                🚀 Primeiros Passos
              </Typography>
              <Box component="ol" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Cadastre seus perfis profissionais</strong> em "Profissionais" e configure os serviços oferecidos
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Adicione seus locais de atendimento</strong> em "Locais" (clínica, consultório, etc.)
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Configure suas agendas</strong> em "Agendas", definindo horários e turnos de trabalho
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Gerencie suas ausências</strong> em "Ausências" (férias, feriados, compromissos)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Card: Recursos Principais */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold">Recursos Principais</Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Múltiplos perfis profissionais
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Gestão de múltiplos locais
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Configuração flexível de horários
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Bloqueio de períodos (férias, etc.)
                </Typography>
                <Typography component="li" variant="body2">
                  Geração automática de slots de agendamento
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card: 💡 Dicas - Mostrar apenas se tem agendas */}
        {stats.scheduleList?.length > 0 && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                💡 Dicas
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Use nomes descritivos para facilitar identificação
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Configure intervalos entre consultas se necessário
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Bloqueie feriados e períodos de férias com antecedência
                </Typography>
                <Typography component="li" variant="body2">
                  Mantenha seus dados atualizados para melhor experiência
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <ServicesManagerDialog
        open={servicesManagerOpen}
        onClose={() => setServicesManagerOpen(false)}
      />

      <AppSnackbar snackbar={copyInfo} onClose={hideSnackbar} autoHideDuration={2000} />
    </PageLayout>
  );
}
